// Shared markdown wrapper: intraword-underscore escaping + element overrides (tables, code, blockquote, headings, lists) shared across all Kelly surfaces.

import Markdown, { RuleType } from "markdown-to-jsx"
import type { MarkdownToJSX } from "markdown-to-jsx"
import { styled } from "@mui/material"
import type { CSSObject, SxProps, Theme } from "@mui/material"
import { ChatCodeBlock, chatCodeBlockClasses } from "@mui/x-chat"
import { Fragment, useMemo } from "react"
import type { ComponentProps } from "react"
import { MarkdownErrorBoundary } from "./markdown-error-boundary"

// Exempt segments — closed fence, streaming (unterminated) fence, inline code, bare URL — pass through unescaped.
const EXEMPT_SEGMENT = /(```[\s\S]*?```|```[\s\S]*$|`[^`\n]+`|https?:\/\/[^\s)]+)/g
// Underscores with a letter/digit on BOTH sides are not emphasis delimiters (CommonMark) — escape the run.
const INTRAWORD_UNDERSCORES = /(?<=[\p{L}\p{N}])_+(?=[\p{L}\p{N}])/gu

export function escapeIntrawordUnderscores(markdown: string): string {
  return markdown
    .split(EXEMPT_SEGMENT)
    .map((segment, index) => (index % 2 === 1 ? segment : segment.replace(INTRAWORD_UNDERSCORES, (run) => run.replace(/_/g, "\\_"))))
    .join("")
}

// Overflow container: outer border stays put while a too-wide table scrolls horizontally inside it.
const TableScroller = styled("div")({
  overflowX: "auto",
  marginBlock: "1.25rem",
  // Longhands, not the `border` shorthand: jsdom drops a shorthand carrying a var(), which blinds the unit test.
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "var(--app-overlay-border)",
  borderRadius: "6px",
})

// Row rules only, no column lines; header uses overlay token (--app-divider too faint in dark mode).
const MarkdownTable = styled("table")({
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "inherit",
  "& th, & td": {
    padding: "0.4375rem 0.75rem",
    textAlign: "start",
    verticalAlign: "top",
    borderBlockEnd: "1px solid var(--app-divider)",
  },
  // Own fill needs own foreground, as on the code card — the band inherits the user bubble's white contrast text.
  "& th": { fontWeight: 600, backgroundColor: "var(--app-bg-muted)", color: "var(--app-text-primary)" },
  "& thead th": { borderBlockEndColor: "var(--app-overlay-border)" },
  // The container's border closes the table, so a rule on the last row would double it.
  "& tbody tr:last-of-type td": { borderBlockEnd: "none" },
  // Hover tint on body rows only; primary-based so it can't be confused with the neutral header band.
  "& tbody tr:hover td": { backgroundColor: "color-mix(in srgb, var(--app-primary) 10%, transparent)" },
})

function ScrollableTable({ children, ...props }: ComponentProps<"table">) {
  return (
    <TableScroller>
      <MarkdownTable {...props}>{children}</MarkdownTable>
    </TableScroller>
  )
}

// Browser default is a 40px indent only; replace with conventional rule + muted text.
const MarkdownBlockquote = styled("blockquote")({
  marginBlock: "1rem",
  marginInline: 0,
  paddingInlineStart: "0.875rem",
  borderInlineStart: "3px solid var(--app-overlay-border)",
  color: "var(--app-text-secondary)",
  // markdown-to-jsx renders `> [!NOTE]` alerts with a `<header>` child; weight and size set it apart from the
  // quote body. No severity colours — the keyword itself carries the severity.
  "& > header": {
    fontWeight: 600,
    textTransform: "uppercase",
    fontSize: "0.8em",
    letterSpacing: "0.04em",
    marginBlockEnd: "0.25rem",
    color: "var(--app-text-primary)",
  },
})

// Markers lie OUTSIDE the content box, so this inline inset IS the marker column — em-relative, and wide enough
// for the "10." a numbered answer reaches.
const LIST = {
  "&&&": { marginBlockStart: "1em", marginBlockEnd: "1em", paddingInlineStart: "1.75em" },
  // Between items only. The list has no block padding or border, so a margin on the first or last item would
  // collapse straight through it and re-open the edge gap the clamps below just closed.
  "&&& li + li": { marginBlockStart: "0.25em" },
  // A nested list is a child of its parent <li>, so the outer rhythm would double-space it.
  "&&& ul, &&& ol": { marginBlockStart: 0, marginBlockEnd: 0 },
  // The third `&` also out-ranks the container's own `& > :first-child` clamp, so re-yield it here.
  "&&&:first-child": { marginBlockStart: 0 },
  "&&&:last-child": { marginBlockEnd: 0 },
} satisfies CSSObject

const MarkdownOrderedList = styled("ol")(LIST)
const MarkdownUnorderedList = styled("ul")(LIST)

// Global margin reset (index.scss) zeroes rhythm; browser defaults (2em/1.5em) too loud — em-relative scale.
const HEADING = { fontWeight: 600, lineHeight: 1.3, marginBlock: "1.25em 0.5em" } as const
const MarkdownH1 = styled("h1")({ ...HEADING, fontSize: "1.35em" })
const MarkdownH2 = styled("h2")({ ...HEADING, fontSize: "1.2em" })
const MarkdownH3 = styled("h3")({ ...HEADING, fontSize: "1.05em" })
const MarkdownH4 = styled("h4")({ ...HEADING, fontSize: "1em" })
// h5/h6 default *below* body size; hold them at body size and let weight carry the level.
const MarkdownH5 = styled("h5")({ ...HEADING, fontSize: "1em" })
const MarkdownH6 = styled("h6")({ ...HEADING, fontSize: "1em" })

const MarkdownRule = styled("hr")({
  marginBlock: "1.25rem",
  border: "none",
  borderBlockStart: "1px solid var(--app-divider)",
})

// The library's bubble rule resets card padding/bg; doubled specificity overrides it so all four surfaces share one inset.
const CODE_BLOCK_SX = {
  [`&.${chatCodeBlockClasses.root}.${chatCodeBlockClasses.root}`]: {
    borderRadius: "6px",
    backgroundColor: "var(--app-bg-muted)",
    // Own fill needs own foreground: a user-sent fence inherited the bubble's white contrast text — white on white.
    color: "var(--app-text-primary)",
  },
  [`& .${chatCodeBlockClasses.pre}.${chatCodeBlockClasses.pre}`]: { padding: "1rem", background: "none" },
  [`& .${chatCodeBlockClasses.header}.${chatCodeBlockClasses.header}`]: { paddingInline: "1rem 0.5rem" },
  // The language label is the card's only caption; weight carries it without making it louder than the code.
  [`& .${chatCodeBlockClasses.languageLabel}`]: { fontWeight: 600 },
} satisfies SxProps<Theme>

// Routes fenced code to the library's themed code card — same one the text part uses, so all paths share one treatment.
const renderCodeBlock: NonNullable<MarkdownToJSX.Options["renderRule"]> = (next, node, _renderChildren, state) => {
  if (node.type === RuleType.codeBlock) {
    return (
      <ChatCodeBlock key={state.key} language={node.lang || undefined} sx={CODE_BLOCK_SX}>
        {node.text}
      </ChatCodeBlock>
    )
  }
  return next()
}

export const MARKDOWN_ELEMENT_OVERRIDES: MarkdownToJSX.Overrides = {
  table: { component: ScrollableTable },
  blockquote: { component: MarkdownBlockquote },
  ol: { component: MarkdownOrderedList },
  ul: { component: MarkdownUnorderedList },
  h1: { component: MarkdownH1 },
  h2: { component: MarkdownH2 },
  h3: { component: MarkdownH3 },
  h4: { component: MarkdownH4 },
  h5: { component: MarkdownH5 },
  h6: { component: MarkdownH6 },
  hr: { component: MarkdownRule },
}

export const MARKDOWN_ELEMENT_RULE = renderCodeBlock

const SAFE_URL_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"]

export function sanitizeMarkdownUrl(value: string): string | null {
  try {
    return SAFE_URL_PROTOCOLS.includes(new URL(value).protocol) ? value : null
  } catch {
    return !value.includes(":") && !/^[/\\]{2}/.test(value) ? value : null
  }
}

// Chain two render rules so a caller's rule runs first and falls through to ours, then to the library default.
// Replacing instead of composing would silently drop the code card for any caller that sets renderRule.
function composeRenderRules(
  outer: NonNullable<MarkdownToJSX.Options["renderRule"]>,
  inner: NonNullable<MarkdownToJSX.Options["renderRule"]>,
): NonNullable<MarkdownToJSX.Options["renderRule"]> {
  return (next, node, renderChildren, state) =>
    outer(() => inner(next, node, renderChildren, state), node, renderChildren, state)
}

const CHAT_MARKDOWN_OPTIONS: MarkdownToJSX.Options = {
  // Fragment wrapper: no extra DOM node between the container's :first-child/:last-child clamps and actual markdown elements.
  forceBlock: true,
  wrapper: Fragment,
  overrides: MARKDOWN_ELEMENT_OVERRIDES,
  renderRule: renderCodeBlock,
}

interface ChatMarkdownProps { children: string, options?: MarkdownToJSX.Options }

// Its own component so the escape runs below the boundary, not while ChatMarkdown builds this element's props.
function EscapedMarkdown({ source, options }: { source: string, options: MarkdownToJSX.Options }) {
  const escaped = useMemo(() => escapeIntrawordUnderscores(source), [source])
  return <Markdown options={options}>{escaped}</Markdown>
}

// Merge caller options over the shared presentation so disableParsingRawHTML/etc. don't silently drop table/code treatment.
export function ChatMarkdown({ children, options }: ChatMarkdownProps) {
  const merged = useMemo(
    () => {
      if (options == null) return CHAT_MARKDOWN_OPTIONS
      const callerRule = options.renderRule
      return {
        ...CHAT_MARKDOWN_OPTIONS,
        ...options,
        renderRule: callerRule ? composeRenderRules(callerRule, renderCodeBlock) : renderCodeBlock,
        overrides: { ...MARKDOWN_ELEMENT_OVERRIDES, ...options.overrides },
      }
    },
    [options],
  )
  return (
    <MarkdownErrorBoundary source={children}>
      <EscapedMarkdown source={children} options={merged} />
    </MarkdownErrorBoundary>
  )
}
